import { useParams, Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  IProjectSolution,
  qoProjectSolutions,
} from "../../utils/queryOptions";
import { useSqids } from "../../hooks/useSqids";
import { ArrowLeft, Copy, CheckCircle } from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { codeToHtml } from "shiki";

export default function ProjectSolutions() {
  const { projectSlugWithId } = useParams();
  const sqids = useSqids();
  const encodedId = projectSlugWithId!.split("-").pop();
  const projectId = sqids.decode(encodedId!);

  const { data, isLoading } = useQuery(
    qoProjectSolutions(Number(projectId))
  );

  const projectTitle = data?.project_title || projectSlugWithId!.split("-").slice(0, -1).join(" ");
  const solutions = data?.solutions || [];

  return (
    <main className="max-w-6xl mx-auto px-3 sm:px-4 pt-4 pb-20 md:pb-36">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-white/40 hover:text-white/70 mb-6 text-sm transition-colors"
      >
        <ArrowLeft size={16} weight="bold" />
        Ana Sayfa
      </Link>

      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
          {projectTitle}
        </h1>
        <p className="text-white/40 text-sm mt-1">Proje Çözümleri</p>
      </div>

      {isLoading && (
        <div className="text-white/40 text-sm animate-pulse">
          Yükleniyor...
        </div>
      )}

      {!isLoading && solutions.length === 0 && (
        <div className="box rounded-2xl p-10 text-center">
          <p className="text-white/30">Henüz açılmış çözüm bulunmuyor.</p>
        </div>
      )}

      {solutions.length > 0 && (
        <div className="space-y-4">
          {solutions.map((s) => (
            <SolutionCard key={s.question_number} solution={s} />
          ))}
        </div>
      )}
    </main>
  );
}

function SolutionCard({ solution }: { solution: IProjectSolution }) {
  const [highlighted, setHighlighted] = useState<string>("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const langMap: Record<string, string> = {
      sql: "sql",
      javascript: "javascript",
      python: "python",
      html: "html",
      css: "css",
    };
    const lang = langMap[solution.language] || "javascript";
    codeToHtml(solution.solution_code.trim(), {
      lang,
      theme: "ayu-dark",
    }).then(setHighlighted);
  }, [solution.solution_code, solution.language]);

  const handleCopy = () => {
    navigator.clipboard.writeText(solution.solution_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="panel border rounded-xl overflow-hidden">
      {/* header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {solution.language === "sql" ? (
            <>
              <span className="shrink-0 font-mono text-xs font-semibold bg-myOrange/15 text-myOrange px-2 py-0.5 rounded">
                Q{solution.question_number}
              </span>
              <span className="text-white/70 text-sm truncate">
                {solution.title}
              </span>
            </>
          ) : (
            <span className="shrink-0 font-mono text-xs font-semibold bg-myOrange/15 text-myOrange px-2 py-0.5 rounded">
              {solution.title}
            </span>
          )}
        </div>

        <button
          onClick={handleCopy}
          className="shrink-0 ml-3 inline-flex items-center gap-1 text-white/25 hover:text-white/60 text-xs transition-colors cursor-pointer"
        >
          {copied ? (
            <>
              <CheckCircle size={14} weight="fill" className="text-myGreen" />
              <span className="text-myGreen hidden sm:inline">Kopyalandi</span>
            </>
          ) : (
            <>
              <Copy size={14} />
              <span className="hidden sm:inline">Kopyala</span>
            </>
          )}
        </button>
      </div>

      {/* code */}
      <div
        className="px-4 py-3 overflow-x-auto text-sm leading-relaxed [&_pre]:!bg-transparent [&_code]:!bg-transparent [&_.shiki]:!p-0 [&_.shiki]:!border-0 [&_.shiki]:!mt-0"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </div>
  );
}
